import { MenuButton, MenuItemWithIcon } from "@msys/ui";
import { BugReport as BugReportIcon } from "@mui/icons-material";
import { Divider } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { useNavigate } from "react-router-dom";
import { useUserData } from "../../../auth/useUserData.js";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByOrganisationPermissionWithDebug } from "../../../auth/RestrictedByOrganisationPermission.js";
import { DeleteTemplateMenuItem } from "../buttons/DeleteTemplateMenuItem.js";
import { DuplicateTemplateMenuItem } from "../buttons/DuplicateTemplateMenuItem.js";
import { ExportTemplatesMenuItem } from "../buttons/ExportTemplatesMenuItem.js";
import { TemplateBulkUpdateProductsButton } from "../buttons/TemplateBulkUpdateProductsButton.js";
import { TemplateRecomputeExpressionDependencyGraphButton } from "../buttons/TemplateRecomputeExpressionDependencyGraphButton.js";
import { QuoteTemplateManageVersionsMenuItem } from "./buttons/QuoteTemplateManageVersionsMenuItem.js";
import { ToggleQuoteTemplateBindingMenuItem } from "./buttons/ToggleQuoteTemplateBindingMenuItem.js";
import { TemplateQuoteTemplateLatestMenu_QuoteTemplateFragment } from "./TemplatesQuoteTemplateLatestMenu.generated.js";

interface Props {
  doc: TemplateQuoteTemplateLatestMenu_QuoteTemplateFragment;
  pathToDoc: string;
  pathToDocList: string;
  pathToDocVersion: string;
  onDuplicateRefetchQueries?: string[];
  onUpdateDataRefetchQueries?: string[];
}

export const TemplatesQuoteTemplateLatestMenu = ({
  doc,
  pathToDoc,
  pathToDocList,
  pathToDocVersion,
  onDuplicateRefetchQueries,
  onUpdateDataRefetchQueries,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslate(["Quote", "Global"]);
  const viewer = useUserData().currentUser;
  const isOwnTemplate =
    doc.owningSystemOrganisationId === viewer?.organisation.id;
  return (
    <MenuButton>
      <RestrictedByOrganisationPermissionWithDebug
        permission="template_settings_update"
        otherwise={
          <ToggleQuoteTemplateBindingMenuItem
            doc={doc}
            docId={doc.id}
            disabled={true}
          />
        }
      >
        <ToggleQuoteTemplateBindingMenuItem doc={doc} docId={doc.id} />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByCapabilityWithDebug capability="templateImportExport">
        <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
          <Divider />
          <ExportTemplatesMenuItem
            docIds={[
              {
                docId: doc.id,
                versionNumber: doc.resolvedAsReadModelVersionNumber ?? null,
              },
            ]}
            title={t("Export template", { ns: "Quote" })}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      </RestrictedByCapabilityWithDebug>
      {isOwnTemplate && (
        <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
          <DuplicateTemplateMenuItem
            doc={doc}
            pathToDocList={pathToDocList}
            onDuplicateRefetchQueries={onDuplicateRefetchQueries}
          />
        </RestrictedByOrganisationPermissionWithDebug>
      )}
      <RestrictedByCapabilityWithDebug capability="pim">
        <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
          <TemplateBulkUpdateProductsButton docId={doc.id} />
        </RestrictedByOrganisationPermissionWithDebug>
      </RestrictedByCapabilityWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
        <QuoteTemplateManageVersionsMenuItem
          docId={doc.id}
          pathToDoc={pathToDoc}
          refetchQueries={onUpdateDataRefetchQueries}
        />
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
        <Divider />
        <TemplateRecomputeExpressionDependencyGraphButton docId={doc.id} />
        <RestrictedByCapabilityWithDebug capability="expressions">
          <MenuItemWithIcon
            icon={<BugReportIcon />}
            onClick={() => navigate(`${pathToDocVersion}/troubleshoot`)}
          >
            {t("Troubleshoot", { ns: "Global" })}
          </MenuItemWithIcon>
        </RestrictedByCapabilityWithDebug>
      </RestrictedByOrganisationPermissionWithDebug>
      <RestrictedByOrganisationPermissionWithDebug permission="template_document_update">
        <Divider />
        <DeleteTemplateMenuItem doc={doc} pathToDocList={pathToDocList} />
      </RestrictedByOrganisationPermissionWithDebug>
    </MenuButton>
  );
};
